import { intlLocale, type Locale, type NumeralSystem } from "@khazzin/i18n";

/**
 * How an analytics figure reads (EP13-S01), shared by the Admin Console and
 * the Store Portal (EP10-S13). The API serves counts and money as integers and
 * ratios as basis points; nothing here does arithmetic beyond the one division
 * to a fraction.
 */

/** Basis points as a percentage with one decimal: 6667 → "66.7%"; none → "—". */
export function formatRatio(
	bps: number | null | undefined,
	locale: Locale,
	numerals: NumeralSystem = "latn",
): string {
	if (bps === null || bps === undefined) {
		return "—";
	}
	return new Intl.NumberFormat(intlLocale(locale, numerals), {
		style: "percent",
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	}).format(bps / 10000);
}

/**
 * An org-local day, `YYYY-MM-DD`, as a short axis label: "3 Sep". Read at UTC
 * midnight and written in UTC, because the string already is the day and no
 * zone may move it.
 */
export function formatDay(day: string, locale: Locale, numerals: NumeralSystem = "latn"): string {
	return new Intl.DateTimeFormat(intlLocale(locale, numerals), {
		day: "numeric",
		month: "short",
		timeZone: "UTC",
	}).format(new Date(`${day}T00:00:00Z`));
}

/** A count as it reads in this locale: 1,234. */
export function formatCount(
	value: number,
	locale: Locale,
	numerals: NumeralSystem = "latn",
): string {
	return new Intl.NumberFormat(intlLocale(locale, numerals)).format(value);
}
